// 📂 src/hooks/useMoney.ts
import { useAuth } from '@/backend/context/auth-management';
import { useTasa } from '@/backend/firebase/hooks';
import { type CurrenciesType } from '@/shared/schema';
import { MoneyUtils } from '@/shared/utils/money-utils';

export const useMoney = () => {
  const { data: rates, isLoading } = useTasa();
  const profile = useAuth((s) => s.profile);

  const localCurrency = profile?.currencyConfig?.localCurrency || 'BS';
  const multiCurrency = profile?.currencyConfig?.multiCurrency ?? true;

  const convert = (
    amount: number,
    from: CurrenciesType,
    to: CurrenciesType,
  ): number => {
    if (!rates) {
      return amount;
    }
    return MoneyUtils.convert(amount, from, to, rates);
  };

  const toLocal = (amount: number, from: CurrenciesType): number => {
    return convert(amount, from, localCurrency);
  };

  const toBs = (amount: number, from: CurrenciesType): number => {
    return convert(amount, from, 'BS');
  };

  const toEur = (amount: number, from: CurrenciesType): number => {
    return convert(amount, from, 'EUR');
  };

  const toUsd = (amount: number, from: CurrenciesType): number => {
    return convert(amount, from, 'USD');
  };

  const format = (amount: number, currency: CurrenciesType): string => {
    return MoneyUtils.format(amount, currency);
  };

  const formatToLocal = (amount: number, from: CurrenciesType): string => {
    return formatConverted(amount, from, localCurrency);
  };

  const formatConverted = (
    amount: number,
    from: CurrenciesType,
    to: CurrenciesType,
  ): string => {
    const converted = convert(amount, from, to);
    return format(converted, to);
  };

  const isMultiCurrency = (currency: CurrenciesType): boolean => {
    return MoneyUtils.isMultiCurrency(currency);
  };

  const getMetadata = (currency: CurrenciesType) => {
    return MoneyUtils.getMetadata(currency);
  };

  return {
    convert,
    toLocal,
    toBs,
    toEur,
    toUsd,
    format,
    rates,
    isLoading,
    formatConverted,
    formatToLocal,
    isMultiCurrency,
    getMetadata,
    localCurrency,
    multiCurrency,
    // Tasas individuales para acceso directo
    currentTasa: rates?.currentTasa || 0,
    euro: (rates as any)?.euro || 0,
    copTasa: (rates as any)?.copTasa || 4000,
    localTasa:
      localCurrency === 'COP'
        ? (rates as any)?.copTasa || 4000
        : rates?.currentTasa || 0,
    eurToUsd: rates?.eurToUsd || 1.08,
    eurToBs: rates ? MoneyUtils.round(rates.currentTasa * rates.eurToUsd) : 0,
  };
};
